import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Menu, Typography, Button } from '@mui/material';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

type DateRangeValue = [Date | null, Date | null];

interface SelectDatePickerProps {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
}

interface StaticRange {
  label: string;
  range: () => { startDate: Date; endDate: Date };
}

const createStaticRanges = (): StaticRange[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return [
    {
      label: 'Today',
      range: () => ({
        startDate: today,
        endDate: today,
      }),
    },
    {
      label: 'Yesterday',
      range: () => {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return {
          startDate: yesterday,
          endDate: yesterday,
        };
      },
    },
    {
      label: 'Last 7 days',
      range: () => ({
        startDate: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
        endDate: today,
      }),
    },
    {
      label: 'This week',
      range: () => {
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay(); // 0 = Sunday
        startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          startDate: startOfWeek,
          endDate: endOfWeek,
        };
      },
    },
    {
      label: 'Last 30 days',
      range: () => ({
        startDate: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
        endDate: today,
      }),
    },
    {
      label: 'This month',
      range: () => {
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { startDate, endDate };
      },
    },
    {
      label: 'Last month',
      range: () => {
        const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate, endDate };
      },
    },
    {
      label: 'Custom',
      range: () => ({
        startDate: today,
        endDate: today,
      }),
    },
  ];
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isSameRange = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  isSameDay(aStart, bStart) && isSameDay(aEnd, bEnd);

const formatDate = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const formatDateRangeForDisplay = (value: DateRangeValue): string => {
  const [start, end] = value;
  if (!start || !end) {
    return 'Date Range';
  }
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const SelectDatePicker: React.FC<SelectDatePickerProps> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [isCustomMode, setIsCustomMode] = useState<boolean>(false);
  const [selectionRange, setSelectionRange] = useState<DateRangeValue>(() => [
    value[0] ?? new Date(),
    value[1] ?? new Date(),
  ]);

  const open = Boolean(anchorEl);

  const staticRanges = useMemo(() => createStaticRanges(), []);

  const heading = useMemo(() => formatDateRangeForDisplay(value), [value]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    const [start, end] = value;
    if (start && end) {
      const match = staticRanges.find((sr) => {
        const range = sr.range();
        return isSameRange(start, end, range.startDate, range.endDate);
      });

      if (match && match.label !== 'Custom') {
        setSelectedLabel(match.label);
        setIsCustomMode(false);
      } else {
        setSelectedLabel('');
        setIsCustomMode(true);
      }

      setSelectionRange([start, end]);
    } else {
      setSelectedLabel('');
      setIsCustomMode(false);
      setSelectionRange([new Date(), new Date()]);
    }
  }, [value, staticRanges]);

  const handleStaticClick = (rangeDef: StaticRange) => {
    if (rangeDef.label === 'Custom') {
      setIsCustomMode(true);
      setSelectedLabel('Custom');
      const { startDate, endDate } = rangeDef.range();
      setSelectionRange([startDate, endDate]);
      return;
    }

    const { startDate, endDate } = rangeDef.range();
    onChange([startDate, endDate]);
    setSelectedLabel(rangeDef.label);
    setIsCustomMode(false);
    handleClose();
  };

  const handleRangeChange = (update: DateRangeValue) => {
    setSelectionRange(update);
  };

  const handleApply = () => {
    const [startDate, endDate] = selectionRange;
    if (!startDate || !endDate) {
      onChange([null, null]);
    } else {
      onChange([startDate, endDate]);
    }
    handleClose();
  };

  const handleReset = () => {
    onChange([null, null]);
    setSelectedLabel('');
    setIsCustomMode(false);
    handleClose();
  };

  return (
    <>
      <Box
        onClick={(event) => setAnchorEl(event.currentTarget as HTMLElement)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid var(--border-Color, #C8CEE3)',
          borderRadius: '8px',
          px: 1.5,
          py: 0,
          maxWidth: '220px',
          minWidth: '100px',
          width: '100%',
          height: 36,
          boxSizing: 'border-box',
          cursor: 'pointer',
          background: "#ffffff",
          '&:hover': {
            borderColor: '#2C6CA0',
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mr: 1,
          }}
        >
          {selectedLabel || heading}
        </Typography>
        <Box
          component="img"
          src="/Icons/Calender.svg"
          alt="calendar"
          sx={{ width: 18, height: 18 }}
        />
      </Box>

      <Menu
        id="audit-date-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
      >
        <Box sx={{ display: 'flex', p: 2, gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 220, gap: 0.5 }}>
            {staticRanges.map((rangeDef) => (
              <Button
                key={rangeDef.label}
                variant={selectedLabel === rangeDef.label ? 'contained' : 'text'}
                color={selectedLabel === rangeDef.label ? 'primary' : 'inherit'}
                size="small"
                onClick={() => handleStaticClick(rangeDef)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                {rangeDef.label}
              </Button>
            ))}
          </Box>

          {(isCustomMode || !value[0] || !value[1]) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <ReactDatePicker
                inline
                selectsRange
                startDate={selectionRange[0]}
                endDate={selectionRange[1]}
                onChange={(update: [Date | null, Date | null]) => handleRangeChange(update)}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={handleReset} size="small" variant="outlined">
                  Reset
                </Button>
                <Button onClick={handleApply} size="small" variant="contained">
                  Apply
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default SelectDatePicker;
